import { X, Pencil, Package, DollarSign, Box, Tag } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import type { Product } from "../../../types/product";
import { useAuth } from "../../../auth/AuthContext";

interface ProductsSidebarProps {
  isOpen: boolean;
  product: Product | null;
  onClose: () => void;
  onEdit: (product: Product) => void;
}

export const ProductsSidebar = ({
  isOpen,
  product,
  onClose,
  onEdit,
}: ProductsSidebarProps) => {
  const { user } = useAuth();
  const currencySymbol = user?.company?.currency_symbol || user?.company?.currency?.currency_symbol || "£";

  if (!product) return null;

  const getStatusStyles = (status: Product["status"]) => {
    if (status === "In Stock") {
      return {
        badge: "bg-emerald-50 text-emerald-700 border-emerald-200",
        dot: "bg-emerald-500",
      };
    }
    if (status === "Low Stock") {
      return {
        badge: "bg-amber-50 text-amber-700 border-amber-200",
        dot: "bg-amber-500",
      };
    }
    return {
      badge: "bg-red-50 text-red-700 border-red-200",
      dot: "bg-red-500",
    };
  };

  const statusStyles = getStatusStyles(product.status);

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex justify-end">
          {/* Backdrop with blur effect */}
          <motion.div
            className="absolute inset-0 bg-black/40 backdrop-blur-[2px] z-[99]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          {/* Sidebar Panel */}
          <motion.div
            className="relative h-screen w-full lg:max-w-lg bg-white shadow-2xl z-[100] flex flex-col"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.3, ease: "easeOut" }}
          >
            {/* Standardized Close Button (Matches QuickPaymentPanel style) */}
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={onClose}
              className="absolute lg:-left-14 right-4 top-4 lg:right-auto lg:top-8 w-10 h-10 flex items-center justify-center rounded-full bg-white text-[#885ABB] shadow-md hover:bg-[#402B69] hover:text-white transition-all z-[110] group"
              aria-label="Close sidebar"
            >
              <X size={20} strokeWidth={3} className="group-hover:rotate-90 transition-transform duration-300" />
            </motion.button>

            {/* Sticky Header */}
            <div className="sticky top-0 z-10 bg-white border-b border-gray-100 shadow-sm">
              <div className="flex items-center justify-between px-4 lg:px-6 py-4 lg:py-5">
                <div className="flex items-center gap-3">
                  <div className="p-2 lg:p-2.5 rounded-xl bg-purple-50 flex-shrink-0">
                    <Package className="w-5 h-5 lg:w-6 lg:h-6 text-[#885ABB]" />
                  </div>
                  <h2 className="text-lg lg:text-xl font-bold text-gray-900 leading-tight">
                    Product Details
                  </h2>
                </div>
              </div>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto custom-scrollbar">
              <div className="p-4 lg:p-6 space-y-6">
                {/* Product Hero Section */}
                <div className="bg-gradient-to-br from-purple-50 via-white to-purple-50/30 rounded-2xl p-6 lg:p-8 border border-purple-100 shadow-sm overflow-hidden relative">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-[#885ABB]/5 rounded-full -mr-16 -mt-16 blur-3xl"></div>
                  <div className="flex flex-col items-center text-center relative z-10">
                    <div className="relative mb-5">
                      <div className="absolute inset-0 bg-purple-200 rounded-2xl blur-xl opacity-20 animate-pulse"></div>
                      <img
                        src={product.image || "/placeholder.png"}
                        alt={product.name}
                        className="relative w-32 h-32 lg:w-40 lg:h-40 object-cover rounded-2xl shadow-xl border-4 border-white"
                      />
                    </div>
                    <h3 className="text-2xl lg:text-3xl font-extrabold text-[#402B69] mb-1 leading-tight">
                      {product.name}
                    </h3>
                    <p className="text-sm lg:text-base font-bold text-gray-400 uppercase tracking-widest mb-4">
                      {product.brand}
                    </p>
                    <span
                      className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs lg:text-sm font-bold border shadow-sm ${statusStyles.badge}`}
                    >
                      <span
                        className={`h-2 w-2 rounded-full ${statusStyles.dot} animate-pulse`}
                      />
                      {product.status}
                    </span>
                  </div>
                </div>

                {/* Basic Info Section */}
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                  <div className="bg-gray-50/50 px-5 py-4 border-b border-gray-100">
                    <h4 className="text-[10px] lg:text-xs font-bold text-gray-400 uppercase tracking-[0.2em] flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#885ABB]"></div>
                      Basic Information
                    </h4>
                  </div>
                  <div className="p-5">
                    <dl className="space-y-4">
                      <div className="flex justify-between items-center group">
                        <dt className="text-sm text-gray-500 font-medium flex items-center gap-2.5">
                          <Tag className="w-4 h-4 text-gray-300" />
                          Brand
                        </dt>
                        <dd className="text-sm lg:text-base font-bold text-[#402B69]">
                          {product.brand || "—"}
                        </dd>
                      </div>
                      <div className="flex justify-between items-center group">
                        <dt className="text-sm text-gray-500 font-medium flex items-center gap-2.5">
                          <Box className="w-4 h-4 text-gray-300" />
                          Location
                        </dt>
                        <dd className="text-sm lg:text-base font-bold text-[#402B69]">
                          {(product as any).locationId || (product as any).location_id || "—"}
                        </dd>
                      </div>
                      <div className="flex justify-between items-center group">
                        <dt className="text-sm text-gray-500 font-medium flex items-center gap-2.5">
                          <Package className="w-4 h-4 text-gray-300" />
                          Stock Quantity
                        </dt>
                        <dd className="text-sm font-bold text-[#402B69]">
                          <span className="inline-flex items-center px-3 py-1 rounded-lg bg-purple-50 text-[#885ABB] border border-purple-100 font-extrabold">
                            {product.stock ?? 0} units
                          </span>
                        </dd>
                      </div>
                      <div className="pt-4 mt-4 border-t border-gray-50 flex justify-between items-center group">
                        <dt className="text-sm text-gray-500 font-medium flex items-center gap-2.5">
                          <DollarSign className="w-4 h-4 text-gray-300" />
                          Retail Price
                        </dt>
                        <dd className="text-xl lg:text-2xl font-black text-[#885ABB]">
                          {currencySymbol}{product.price.toLocaleString()}
                        </dd>
                      </div>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            {/* Sticky Footer with Actions */}
            <div className="sticky bottom-0 bg-white/80 backdrop-blur-md border-t border-gray-100 p-4 lg:p-6 shadow-[0_-4px_20px_rgba(0,0,0,0.03)] flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => {
                  onEdit(product);
                  onClose();
                }}
                className="flex-[2] h-12 lg:h-14 bg-[#885ABB] text-white rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-[#7748A4] transition-all duration-300 shadow-lg shadow-purple-200 transform hover:-translate-y-0.5 active:translate-y-0 text-sm lg:text-base"
              >
                <Pencil className="w-4 h-4 lg:w-5 lg:h-5" />
                Edit Product
              </button>

              <button
                onClick={onClose}
                className="flex-1 h-12 lg:h-14 bg-gray-50 text-gray-600 rounded-xl font-bold hover:bg-gray-100 transition-all duration-300 border border-gray-100 text-sm lg:text-base"
              >
                Close
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
